/**
 * CompanionOnboardingContainer.tsx
 * Business logic container for the Companion onboarding flow.
 *
 * Responsibilities:
 * - Manage onboarding modal visibility
 * - Handle form submission with GraphQL mutation
 * - Trigger refetch of user data after completion
 * - Handle loading and error states
 *
 * Integration:
 * - Uses updateUserLearningPreferences mutation which saves preferences AND sets isOnboarded: true
 * - Integrates with useCompanionOnboardingForm hook for form state
 * - Phone verification happens in OnboardingStep5 component via verifyReminderPhone API
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CompanionOnboardingModal from "../../components/companion/onboarding/CompanionOnboardingModal";
import { useCompanionOnboardingForm } from "../../hooks/useCompanionOnboardingForm";
import { useUpdateUserLearningPreferencesMutation } from "@axio/graphql/generated/graphql";
import { useAuth } from "../../hooks/useAuth";

interface CompanionOnboardingContainerProps {
  visible: boolean;
  onClose: () => void;
  isCompanionMode?: boolean;
}

function CompanionOnboardingContainer({ visible, onClose, isCompanionMode = true }: CompanionOnboardingContainerProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { meData, refetchMe } = useAuth();
  const [updateUserLearningPreferences] = useUpdateUserLearningPreferencesMutation();

  const goalOptions = [
    { key: "pass_finals", value: t("text.companion_onboarding_goal_pass_finals") },
    { key: "improve_gpa", value: t("text.companion_onboarding_goal_improve_gpa") },
    { key: "complete_prerequisites", value: t("text.companion_onboarding_goal_complete_prerequisites") },
    { key: "stay_on_track", value: t("text.companion_onboarding_goal_stay_on_track") },
  ];

  const {
    currentScreen,
    formData,
    canProceed,
    setValue,
    getValues,
    handleStart,
    handleNext,
    handleBack,
    handleSkip,
    goToSuccess,
    resetForm,
    setPhoneVerified,
    setSmsOptIn,
  } = useCompanionOnboardingForm();

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(undefined);

      const values = getValues();

      // Save onboarding preferences to the database
      // Convert lowercase preference values to uppercase enum values
      const studyTimePreference = values.studyTimePreference
        ? (values.studyTimePreference.toUpperCase() as any)
        : undefined;
      const checkInFrequency = values.checkInFrequency ? (values.checkInFrequency.toUpperCase() as any) : undefined;

      const { data } = await updateUserLearningPreferences({
        variables: {
          input: {
            interests: values.interests ?? [],
            careerInterests: values.careerInterests ?? [],
            skillsToImprove: values.skillsToImprove ?? [],
            learningStyles: values.learningStyles ?? [],
            motivations: values.motivations ?? [],
            semesterGoal: values.semesterGoal ?? "",
            assignmentReminders: values.assignmentReminders ?? true,
            studyTimePreference,
            checkInFrequency,
            textReminders: values.textReminders ?? false,
          },
        },
      });

      if (data?.updateUserLearningPreferences) {
        // The backend mutation already sets isOnboarded: true
        // Show success screen - we'll refetch user data when they click "Start Chatting"
        goToSuccess();
      } else {
        setError("Failed to save onboarding data");
      }
    } catch (err) {
      console.error("Error submitting onboarding:", err);
      setError("An error occurred while saving your preferences. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    // Called when user clicks "Start Chatting" on success screen
    // The onboarding status (isOnboarded: true) was already set by updateUserLearningPreferences
    // Now refetch user data to update the UI, then close the modal
    await refetchMe();
    resetForm();
    onClose();
  };

  const handleModalSkip = () => {
    // When user clicks "Skip" on intro screen, dismiss modal without saving
    // Form is reset so if they open it again later, they start fresh
    resetForm();
    onClose();
  };

  return (
    <CompanionOnboardingModal
      visible={visible}
      currentScreen={currentScreen}
      formData={formData}
      setValue={setValue}
      onStart={handleStart}
      onNext={handleNext}
      onBack={handleBack}
      onSubmit={handleSubmit}
      onSkip={handleModalSkip}
      onComplete={handleComplete}
      canProceed={canProceed}
      isLoading={isLoading}
      error={error}
      goalOptions={goalOptions}
      setPhoneVerified={setPhoneVerified}
      setSmsOptIn={setSmsOptIn}
      isCompanionMode={isCompanionMode}
    />
  );
}

export default CompanionOnboardingContainer;
